import type { BulkActionProgress } from '../types/reducers/progress'
import React from 'react'
import { BulkActionDialog, BulkActionDialogResultActions } from './ui/BulkActionDialog'
import {
    BulkActionStatus,
    type BulkActionStatusCounts,
    type BulkActionStatusPhase,
} from './ui/BulkActionStatus'

interface BulkActionsProgressPreviewDialogProps {
    isOpen: boolean
    data: { [key: string]: BulkActionProgress }
    name?: string
}

export const BulkActionsProgressPreviewDialog = ({
    isOpen,
    data,
    name,
}: BulkActionsProgressPreviewDialogProps) => {
    const types = Object.keys(data)
    // A single bar takes its heading from its own progress mode; the dialog
    // title already names the action. With several entity types in flight the
    // type is the only thing telling the bars apart, so it leads.
    const bars = types.map((type) => ({
        progress: data[type],
        title: types.length === 1 ? undefined : `${type} bulk action`,
    }))

    const waitingForDependency = bars.some(
        ({ progress }) => progress.state === 'waiting_for_dependency',
    )

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={() => undefined}
            title={name ?? 'Bulk action in progress'}
            description="This can take a few minutes for large bulk actions."
            dismissible={false}
        >
            <ProgressPreview
                bars={bars}
                description={
                    waitingForDependency ? <div>Your bulk action is safely queued.</div> : undefined
                }
            />
        </BulkActionDialog>
    )
}

export interface OwnedBulkActionStatusDialogProps {
    isOpen: boolean
    phase: Extract<
        BulkActionStatusPhase,
        'running' | 'succeeded' | 'partially_succeeded' | 'failed'
    >
    title: string
    message?: string
    counts?: BulkActionStatusCounts
    entity?: string
    entityPlural?: string
    progressPercent?: number
    onClose: () => void
}

/** Presentation for the run owned by one initiating BulkActions instance. */
export const OwnedBulkActionStatusDialog = ({
    isOpen,
    phase,
    title,
    message,
    counts,
    entity,
    entityPlural,
    progressPercent,
    onClose,
}: OwnedBulkActionStatusDialogProps) => {
    const running = phase === 'running'

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            dismissible={!running}
            footer={running ? undefined : <BulkActionDialogResultActions onDismiss={onClose} />}
        >
            {/* The dialog header already carries `title`; repeating it in the
                status card produced the same string twice, one line apart. */}
            <BulkActionStatus
                phase={phase}
                title={running ? 'Updating' : null}
                message={message}
                counts={counts}
                entity={entity}
                entityPlural={entityPlural}
                progressPercent={progressPercent}
            />
        </BulkActionDialog>
    )
}

interface ProgressPreviewBar {
    progress: BulkActionProgress
    /** Omit to let the bar title itself by progress mode. */
    title?: string | JSX.Element
}

const dependencyWaitFallback =
    'This action is waiting for a required service and will resume automatically.'

export type ProgressPreviewMode =
    | 'syncing'
    | 'updating'
    | 'indeterminate'
    | 'waiting_for_dependency'

export const getProgressPreviewMode = (progress: BulkActionProgress): ProgressPreviewMode => {
    if (progress.state === 'waiting_for_dependency') {
        return 'waiting_for_dependency'
    }

    if (progress.total_items_count <= 0 || !Number.isFinite(progress.progress)) {
        return 'indeterminate'
    }

    return progress.sync_count !== progress.total_items_count ? 'syncing' : 'updating'
}

const modeHeadings: Record<ProgressPreviewMode, string> = {
    syncing: 'Syncing',
    updating: 'Updating',
    indeterminate: 'Waiting for progress',
    waiting_for_dependency: 'Queued',
}

const ProgressPreviewBar = ({ progress, title }: ProgressPreviewBar) => {
    const mode = getProgressPreviewMode(progress)
    const completed = mode === 'updating' ? progress.update_count : progress.sync_count
    const message =
        mode === 'waiting_for_dependency'
            ? (progress.message ?? dependencyWaitFallback)
            : mode === 'indeterminate'
              ? 'Waiting for progress totals from the bulk-action service.'
              : `${completed.toLocaleString()} of ${progress.total_items_count.toLocaleString()} processed`

    return (
        <BulkActionStatus
            className="mt-[14px]"
            phase="running"
            title={title ?? modeHeadings[mode]}
            message={message}
            progressPercent={
                mode === 'indeterminate' || mode === 'waiting_for_dependency'
                    ? undefined
                    : progress.progress
            }
        />
    )
}

interface ProgressPreviewProps {
    description?: string | JSX.Element
    bars: ProgressPreviewBar[]
}

export const ProgressPreview = ({ description, bars }: ProgressPreviewProps) => {
    return (
        <div>
            {description}

            {bars.map((bar, i) => (
                <ProgressPreviewBar key={i} progress={bar.progress} title={bar.title} />
            ))}
        </div>
    )
}
